import axios from '../../../api/axios';
import React, { useEffect, useState, useRef } from "react";
import styled from 'styled-components';
import ChatHistoryMessage from './ChatHistoryMessage';
import ChatHistoryHeader from './ChatHistoryHeader';

interface ChatProps {
  userId: string | null;
  username: string | null;
  conversationID: string;
  conversationType: number;
  profileName: string;
  teleconsults: any[];
}

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  background-color: #ffffff;
`;

const ChatBox = styled.div`
  width: calc(100vw - 32px);
  background: white;
  border-radius: 12px;
  border: 1px solid #e0e0e0;
  font-family: Arial, sans-serif;
  overflow: hidden;
  margin: 0 auto 20px auto;
`;

const UserHeader = styled.div`
  padding: 10px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
`;

const ProfileText = styled.div`
  font-weight: bold;
  font-size: 14px;
  color: #333;

  span {
    color: #999;
    font-weight: normal;
    margin-left: 5px;
  }
`;

const ChatSection = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
`;

const SearchInput = styled.input`
  width: calc(100% - 32px);
  margin: 12px 16px;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  outline: none;
  font-size: 14px;

  &:focus {
    border-color: #00bfa5;
  }
`;

const SectionTitle = styled.div`
  color: #00bfa5;
  font-weight: bold;
  padding: 12px 16px;
  font-size: 14px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
`;

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }
`;

const ChatAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 12px;
`;

const ChatContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ChatName = styled.span`
  font-weight: bold;
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
`;

const ChatDate = styled.span`
  font-size: 12px;
  color: #888;
`;

const MessagesContainer = styled.div`
  flex: 1;
  max-height: 60vh;
  min-height: 60vh;
  padding: 16px;
  overflow-y: auto;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
`;

const EmptyState = styled.div`
  padding: 16px;
  color: #666;
  text-align: center;
  font-size: 14px;
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 20px;
  color: #666;
`;

interface Chat {
  id: string | number;
  name: string;
  date: string;
  avatar: string;
  roomId: number;
  specialty: string;
  fromUserId: string;
  toUserId: string;
}

interface Message {
  message: string;
  timestamp: string;
  isDoctor: boolean;
  imageUrl?: string;
  fileName?: string;
  type?: string;
}

const extractUserId = (value: string | undefined): number | null => {
  if (!value) return null;
  const match = value.match(/^LMS(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

const ChatHistoryList: React.FC<ChatProps> = ({ 
  userId, 
  username, 
  conversationID, 
  conversationType, 
  profileName, 
  teleconsults 
}) => {
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [doctorId, setDoctorId] = useState<number | null>(null);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const storedData = sessionStorage.getItem('userData');
  const userData = storedData ? JSON.parse(storedData) : null;
  const token = userData ? userData.token : null;

  const formatTimestamp = (dateString: string | number) => {
    if (!dateString) return '--:--';
    try {
      return new Date(dateString).toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } catch (e) {
      return '--:--';
    }
  };

  const parseDateString = (dateStr: string) => {
    if (!dateStr) return new Date();
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    }
    return new Date(dateStr);
  };

  const parseMessageBody = (msgBody: string | object) => {
    if (typeof msgBody !== "string") return msgBody;

    if (msgBody.startsWith("accepted|")) {
      return {
        type: "system",
        subtype: "accepted",
        message: msgBody 
      };
    }

    if (msgBody.startsWith("end|")) {
      return {
        type: "system",
        subtype: "end",
        message: msgBody 
      };
    }

    try {
      return JSON.parse(msgBody);
    } catch {
      return { type: "text", message: msgBody };
    }
  };

  const fetchChats = async (roomId: number, fromUserId: string, toUserId: string) => {
    setLoading(true);
    try {
      const response = await axios.get(`chat-histories/${roomId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const rawMessages = response.data?.data?.List || [];
      
      const startMsg = rawMessages.find((item: any) => {
        const msgData = item.List || item;
        const parsedBody = parseMessageBody(msgData.MsgBody || msgData.msg_body);
        return parsedBody?.type === "start";
      });

      let detectedDoctorId: number | null = null;
      if (startMsg) {
        const msgData = startMsg.List || startMsg;
        detectedDoctorId = extractUserId(msgData.Sender || msgData.sender);
      }

      if (!detectedDoctorId) {
        detectedDoctorId = extractUserId(fromUserId);
      }

      setDoctorId(detectedDoctorId);
      
      const formattedMessages: Message[] = rawMessages
        .map((item: any) => {
          const msgData = item.List || item;
          const parsedBody = parseMessageBody(msgData.MsgBody || msgData.msg_body);
          
          if (parsedBody.type === "system") {
            return {
              message: parsedBody.message,
              timestamp: formatTimestamp(msgData.MsgTime || msgData.msg_time || msgData.created_at),
              isDoctor: true,
              type: parsedBody.type,
            };
          }

          if (parsedBody.type === "new-summary") {
            return {
              message: JSON.stringify(parsedBody),
              timestamp: formatTimestamp(msgData.MsgTime || msgData.msg_time || msgData.created_at),
              isDoctor: true,
              type: parsedBody.type,
            };
          }

          if (!parsedBody.message && !parsedBody.Message && parsedBody.type !== "start") {
            return null;
          }

          const sender = msgData.Sender || msgData.sender || msgData.from_user_id;
          const msgTime = msgData.MsgTime || msgData.msg_time || msgData.created_at;
          const senderId = extractUserId(sender);
          const isDoctor = senderId === detectedDoctorId;

          return {
            message: parsedBody.message || parsedBody.Message || JSON.stringify(parsedBody),
            timestamp: formatTimestamp(msgTime),
            isDoctor: isDoctor,
            imageUrl: parsedBody.imageUrl || parsedBody.image_url,
            fileName: parsedBody.fileName || parsedBody.file_name,
            type: parsedBody.type || msgData.MsgType || msgData.msg_type,
          };
        })
        .filter(Boolean) as Message[];
  
      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error fetching chats:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const chats: Chat[] = Array.isArray(teleconsults)
    ? [...teleconsults]
        .sort((a, b) => {
          const dateA = parseDateString(a.date_created);
          const dateB = parseDateString(b.date_created);
          return dateB.getTime() - dateA.getTime();
        })
        .map((item: any) => ({
          id: item.id,
          name: item.doctor_name || 'Tidak diketahui',
          date: item.date_created || '-',
          avatar: 'https://cdn-icons-png.flaticon.com/512/4140/4140051.png',
          roomId: item.id,
          specialty: item.speciality || 'Dokter',
          fromUserId: item.from_user_id,
          toUserId: item.to_user_id,
        }))
    : [];

  const filteredChats = chats.filter(chat =>
    chat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleChatSelect = (chat: Chat) => {
    setSelectedChat(chat);
    fetchChats(chat.roomId, chat.fromUserId, chat.toUserId);
  };

  const handleBackClick = () => {
    setSelectedChat(null);
    setMessages([]);
    setDoctorId(null);
  };

  return (
    <CenteredContainer>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '12px',
        margin: '12px 16px 52px 16px'
      }}>
        {profileName?.trim() && (
          <UserHeader>
            <ProfileText>
              {profileName} <span>(Me)</span>
            </ProfileText>
          </UserHeader>
        )}

        <ChatBox>
          <ChatSection>
            {!selectedChat ? (
              <>
                <SearchInput
                  type="text"
                  placeholder="Cari percakapan"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <SectionTitle>Chat Selesai</SectionTitle>
                {filteredChats.length > 0 ? (
                  filteredChats.map((chat) => (
                    <ChatItem key={chat.id} onClick={() => handleChatSelect(chat)}>
                      <ChatAvatar src={chat.avatar} alt={chat.name} />
                      <ChatContent>
                        <ChatName>{chat.name}</ChatName>
                        <ChatDate>{chat.date}</ChatDate>
                      </ChatContent>
                    </ChatItem>
                  ))
                ) : (
                  <EmptyState>
                    {teleconsults.length === 0 ? 'Tidak ada riwayat chat' : 'Tidak ada percakapan yang cocok'}
                  </EmptyState>
                )}
              </>
            ) : (
              <>
                <ChatHistoryHeader
                  onBack={handleBackClick}
                  chatName={selectedChat.name}
                  chatAvatar={selectedChat.avatar}
                  chatRoomId={selectedChat.roomId}
                  chatSpeciality={selectedChat.specialty}
                />
                <MessagesContainer ref={messagesContainerRef}>
                  {loading ? (
                    <LoadingText>Memuat pesan...</LoadingText>
                  ) : messages.length > 0 ? (
                    messages.map((msg, index) => (
                      <ChatHistoryMessage key={index} {...msg} />
                    ))
                  ) : (
                    <EmptyState>Tidak ada pesan</EmptyState>
                  )}
                  <div ref={messagesEndRef} />
                </MessagesContainer>
              </>
            )}
          </ChatSection>
        </ChatBox>
      </div>
    </CenteredContainer>
  );
};

export default ChatHistoryList;